import { Component, Inject } from '@angular/core';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ConfigService } from 'src/app/services/config/config.service';

export interface AuditCompilerDialogData {
  transformType: number;
  config: any;
  inputCacheKey: string;
  /** Identify which transform entry to persist the audit outcome onto (lastAuditHash/
   * lastAuditResult), driving the top header's blue/green/red state. configGuid is the
   * node's own config FILE guid (transformerconfigPath/{configGuid}.json, holding that
   * node's `transforms` array) -- NOT the refinery config, which has no `transforms` key
   * at all. transformGuid is that specific transform's own entry guid within the file,
   * since one node can chain several transforms (T1, T2, ...). */
  configGuid: string;
  transformGuid: string;
  /** Only read by Column Lookup's own backend audit adapter, to resolve its second,
   * separate lookup/reference farm the same way the live engine does (a cache lookup
   * keyed by orgId/userId/refineryId + the config's own lookupFarm node id). Every other
   * transform type's adapter ignores these. */
  orgId?: string;
  userId?: string;
  refineryId?: string;
}

export interface AuditRuleReport {
  index: number;
  ruleName: string;
  /** Ready-to-display label from the backend's own adapter (e.g. "Find and
   * Replace" for Column Cleaner, "Starts With → Ends Before" for Row
   * Extractor) -- each transform type describes its own rule shape server-
   * side (compiler_audit.py's describe_rule()) since only the adapter knows
   * what that type's rules actually look like. */
  ruleTypeLabel: string;
  generalRowCount: number;
  compiledRowCount: number;
  rowCountMatch: boolean;
  columnsMatch: boolean;
  valuesMatch: boolean;
  match: boolean;
}

export interface AuditReport {
  overallMatch: boolean;
  generalRowCount: number;
  compiledRowCount: number;
  columnsOnlyInGeneral: string[];
  columnsOnlyInCompiled: string[];
  mismatchedColumns: { [column: string]: number };
  ruleReports: AuditRuleReport[];
  elapsedSeconds: number;
  error?: string;
}

/** "Audit Compiler Quality" -- see app/compiled/compiler_audit.py's own docstring
 * for the full backend design. Explicit, on-command diagnostic only; never part
 * of the normal Run Transform / Run Node flow. Confirms compiled-vs-general
 * engine agreement on the transformer's real, current, full input farm, and --
 * on a mismatch -- shows exactly which rule first diverged. */
@Component({
  selector: 'app-audit-compiler-dialog',
  templateUrl: './audit-compiler-dialog.component.html',
  styleUrls: ['./audit-compiler-dialog.component.css'],
  standalone: false,
})
export class AuditCompilerDialogComponent {
  /** 'confirm' (warning + Start button) -> 'running' -> 'results' | 'error' */
  state: 'confirm' | 'running' | 'results' | 'error' = 'confirm';
  report: AuditReport | null = null;
  errorMessage = '';

  constructor(
    private dialogRef: MatDialogRef<AuditCompilerDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: AuditCompilerDialogData,
    private configService: ConfigService,
    private matDialog: MatDialog,
  ) {}

  startAudit(): void {
    if (!this.data.inputCacheKey) {
      this.state = 'error';
      this.errorMessage = 'No input farm is currently cached for this transformer -- run it at least once first.';
      return;
    }
    this.state = 'running';
    this.configService
      .auditCompiler<AuditReport>(this.data.transformType, this.data.config, this.data.inputCacheKey, this.data.configGuid, this.data.transformGuid, this.data.orgId, this.data.userId, this.data.refineryId)
      .then((res) => {
        if ((res as any)?.error) {
          this.state = 'error';
          this.errorMessage = (res as any).error;
          return;
        }
        this.report = res;
        this.state = 'results';
      })
      .catch((err) => {
        this.state = 'error';
        this.errorMessage = err?.error?.error ?? err?.message ?? 'An unexpected error occurred';
      });
  }

  /** Rules the bisection never reached (it stops at the first divergence --
   * everything after that point is operating on already-diverged data in one
   * engine but not the other, so comparing it further would be meaningless). */
  get untestedRuleCount(): number {
    if (!this.report || this.report.overallMatch) return 0;
    const activeRuleCount = (this.data.config?.rules ?? []).filter((r: any) => r.run).length;
    return Math.max(0, activeRuleCount - this.report.ruleReports.length);
  }

  viewRuleDetail(rule: AuditRuleReport): void {
    this.matDialog.open(AuditRuleDetailDialogComponent, {
      width: '480px',
      data: rule,
    });
  }

  close(): void {
    // Pass the completed report back to the caller (transformer-top-header's
    // openAuditCompiler()) so it can immediately reflect the new blue/green/red
    // state without waiting on an unrelated refresh/navigation to re-fetch it --
    // the backend has already persisted lastAuditHash/lastAuditResult by this
    // point (compiled_blueprint.py's audit_compiler(), best-effort).
    this.dialogRef.close(this.state === 'results' ? this.report : undefined);
  }
}

/** Small secondary "pop-up" opened per subtransformer row from the results list. */
@Component({
  selector: 'app-audit-rule-detail-dialog',
  template: `
    <h2 mat-dialog-title>{{ data.ruleName }}</h2>
    <mat-dialog-content>
      <table style="width:100%; border-collapse:collapse;">
        <tr><td class="lbl">Rule type</td><td>{{ data.ruleTypeLabel }}</td></tr>
        <tr><td class="lbl">General engine rows</td><td>{{ data.generalRowCount | number }}</td></tr>
        <tr><td class="lbl">Compiled engine rows</td><td>{{ data.compiledRowCount | number }}</td></tr>
        <tr><td class="lbl">Row count</td><td>{{ data.rowCountMatch ? 'Match' : 'MISMATCH' }}</td></tr>
        <tr><td class="lbl">Columns</td><td>{{ data.columnsMatch ? 'Match' : 'MISMATCH' }}</td></tr>
        <tr><td class="lbl">Values</td><td>{{ data.valuesMatch ? 'Match' : 'MISMATCH' }}</td></tr>
      </table>
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-button mat-dialog-close>Close</button>
    </mat-dialog-actions>
  `,
  styles: ['.lbl { color: #666; padding-right: 16px; white-space: nowrap; }'],
  standalone: false,
})
export class AuditRuleDetailDialogComponent {
  constructor(@Inject(MAT_DIALOG_DATA) public data: AuditRuleReport) {}
}
